import { useEffect, useMemo, useRef, useState } from "react";

import { SHARED_FULL_WEEKS_CRITERIA } from "../../lib/chartTables.js";
import {
  dailyChartAxisLabel,
  dailyChartBarAxisProps,
  dailyChartRotateAxisLabels,
  formatDashboardDayLabelLong,
} from "../../lib/dashboardDayLabel.js";
import { expectedUptimeHoursForDay } from "../../lib/expectedUptime.js";
import {
  formatWeekAxisLabel,
  FULL_WEEKS_EMPTY_MESSAGE,
  groupFullMondayWeeks,
  priorWeekMeasuredUptime,
  shiftReportingDayKey,
  type UptimeWeekBucket,
} from "../../lib/uptimeWeekBuckets.js";
import type {
  DailyUptimeRow,
  FilterState,
  HealthDeviceDailyUptime,
  HealthDeviceOption,
} from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { BarChart, type BarChartItem } from "../charts/BarChart.js";
import { ChartTablePicker } from "./ChartTablePicker.js";

const UPTIME_FILL = "#31c48d";
/** Expected uptime band (capacity − scheduled): same green, faded. */
const EXPECTED_UPTIME_FILL = "rgba(49, 196, 141, 0.28)";
const PRIOR_WEEK_STROKE = "#c5d4f0";
/** Past this, numbers above bars overlap; hover still has the hours. */
const VALUE_LABEL_MAX_BARS = 7;

function formatUptimeHoursLabel(hours: number): string {
  const t = hours.toFixed(1);
  return t.endsWith(".0") ? t.slice(0, -2) : t;
}

/** Uptime as a percent of expected; null when there is no expected target. */
function formatUpVsExpectedPct(upHours: number, expectedHours: number): string | null {
  if (!(expectedHours > 0)) return null;
  return `${Math.round((upHours / expectedHours) * 100)}%`;
}

/** Range totals for the chart label: tables, hours up, expected, up vs expected. */
function uptimeRangeLabel(tables: number, upHours: number, expectedHours: number): string {
  const tablesMeta = tables === 1 ? "1 table" : `${tables} tables`;
  const expected = `${formatUptimeHoursLabel(expectedHours)}h expected`;
  const pct = formatUpVsExpectedPct(upHours, expectedHours);
  const expectedBit = pct ? `${expected} (${pct} up vs expected)` : expected;
  return `${tablesMeta} · ${formatUptimeHoursLabel(upHours)}h up · ${expectedBit}`;
}

/**
 * Week bars: hours plus % on the same line as the **Uptime (%)** title.
 * Fully unmeasured weeks stay an em dash.
 */
function weekBarValueLabel(week: UptimeWeekBucket): Pick<BarChartItem, "valueLabel"> {
  if (week.unmeasuredHours >= week.capacityHours) return { valueLabel: "—" };
  const hours = formatUptimeHoursLabel(week.uptimeHours);
  const pct = formatUpVsExpectedPct(week.uptimeHours, week.expectedHours);
  return { valueLabel: pct ? `${hours} (${pct})` : hours };
}

type DayHours = {
  day: string;
  uptimeHours: number;
  scheduledHours: number;
  capacityHours: number;
  unmeasuredHours: number;
};

/** Fleet-additive sum of selected tables per reporting day (hours). */
function sumSelectedDailyUptime(
  byDevice: HealthDeviceDailyUptime[],
  selectedIds: ReadonlySet<string>,
): DayHours[] {
  const byDay = new Map<
    string,
    { uptimeHours: number; scheduledHours: number; capacityHours: number; unmeasuredHours: number }
  >();
  for (const device of byDevice) {
    if (!selectedIds.has(device.deviceId)) continue;
    for (const row of device.dailyUptime) {
      const prev = byDay.get(row.day) ?? {
        uptimeHours: 0,
        scheduledHours: 0,
        capacityHours: 0,
        unmeasuredHours: 0,
      };
      prev.uptimeHours += Math.max(0, row.uptimeHours);
      prev.scheduledHours += Math.max(0, row.scheduledHours);
      prev.unmeasuredHours += row.unmeasuredHours;
      prev.capacityHours += Math.max(0, row.capacityHours);
      byDay.set(row.day, prev);
    }
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, v]) => ({ day, ...v }));
}

/** Per-device-day expected, then fleet-additive by day (do not apply the rule on summed rows). */
function sumExpectedUptimeByDay(
  byDevice: HealthDeviceDailyUptime[],
  selectedIds: ReadonlySet<string>,
): Map<string, number> {
  const byDay = new Map<string, number>();
  for (const device of byDevice) {
    if (!selectedIds.has(device.deviceId)) continue;
    for (const row of device.dailyUptime) {
      byDay.set(row.day, (byDay.get(row.day) ?? 0) + expectedUptimeHoursForDay(row));
    }
  }
  return byDay;
}

const CRITERIA = `Fleet-additive **uptime hours per reporting day** across the tables selected in this card (default: all named tables in the current venue/device filter).

Platform-observed heartbeat minutes, with scheduled windows (current venue plan when configured; otherwise declared events) taking precedence. Missing minutes are no signal; historical gaps without evidence are not measured. Summed across selected tables.

**Solid green** = actual uptime hours. **Faded green** = **expected uptime** hours that day: fleet **capacity − scheduled downtime** (table-hours that were not scheduled sleep), independent of heartbeat coverage. The gap above actual uptime includes both no signal and historical time that was not measured. Today includes elapsed hours only.

**Dotted line** = uptime on the **same weekday 7 days earlier**. Health loads a **7-day lookback** before the filter window so the line still appears on short ranges (e.g. **7d**); lookback days are not drawn as bars.

**Full weeks** (checkbox, off by default; shared URL \`weeks=1\` — Overview PDF follows): one bar per complete **Monday–Sunday** reporting week whose seven days are all inside the filter window and each have a full day's capacity (today-so-far and future days do not count). Hours are **summed** across those 7 days and the checked tables. Partial weeks at the edges are omitted, so the range label covers only those full weeks. Dotted line = previous Monday–Sunday uptime (lookback included), omitted if any of those 7 days was unmeasured. Axis label is the week span (18–24/8, or 31/8–6/9 across a month).

${SHARED_FULL_WEEKS_CRITERIA}

Table checklist is the shared **tables** URL param (omitted = all, tables=none, or device ids). Setting it here updates Interaction Stints / Uptime Hour, Revenue, Revenue per table, and Stints to payment confirmed, and the other way around. It does **not** change the dashboard Device filter or other Health panels.

Y-axis auto-scales to peak expected-on / uptime / prior-week points. Numbers above bars are **uptime** hours when there are **7 or fewer** days or weeks; past that they are omitted so they do not overlap (hover still shows hours). In **Full weeks** the row title is **Uptime (%)** and each number is hours plus **% up vs expected** (e.g. **142 (98%)**); the week tooltip includes the same percent. The label above that row is range totals for the checked tables: **N tables · Yh up · Zh expected (P% up vs expected)**. Percent is uptime ÷ expected (capacity − scheduled, including unmeasured hours). Day axis labels: **D/M** + weekday initial; Sat/Sun bold white. Dense ranges rotate −90°.

Not a single event_name series; clicks not wired.`;

interface UptimeByDayCardProps {
  dailyUptimeByDevice: HealthDeviceDailyUptime[];
  /** Fallback when by-device is empty but fleet totals exist. */
  dailyUptime: DailyUptimeRow[];
  /** Pre-window fleet days for the −7d line (from Health lookback). */
  lookbackDailyUptime?: DailyUptimeRow[];
  /** Pre-window per-device days for the −7d line with a table subset. */
  lookbackDailyUptimeByDevice?: HealthDeviceDailyUptime[];
  devices: HealthDeviceOption[];
  filters: FilterState;
  /** When false, hides the ⓘ criteria toggle (Overview / PDF). */
  showCriteria?: boolean;
  /**
   * Controlled table include set (Overview shares this between on-screen and print trees).
   * When omitted, selection is local to this card.
   */
  selectedDeviceIds?: ReadonlySet<string>;
  onSelectedDeviceIdsChange?: (next: Set<string>) => void;
  /** Hide the interactive checklist (print copy still shows “N of M tables”). */
  hideTablePicker?: boolean;
  /**
   * Parent already reconciles the shared URL selection against the device list.
   * Skips this card's own reconcile (which would also write a dashboard Device-filter lock
   * back into the shared selection).
   */
  externallyReconciled?: boolean;
  /**
   * Controlled full-weeks grouping (shared URL \`weeks=1\`; Overview PDF follows).
   * When omitted, the checkbox is local to this card.
   */
  fullWeeks?: boolean;
  onFullWeeksChange?: (next: boolean) => void;
  /** Hide the checkbox (print copy still labels “Full weeks” when on). */
  hideWeekToggle?: boolean;
}

function nextSelectionForDeviceList(
  prev: ReadonlySet<string>,
  prevAllIds: readonly string[],
  allIds: readonly string[],
  filterLocksSingle: boolean,
  lockedDeviceId: string,
): Set<string> {
  if (filterLocksSingle) return new Set([lockedDeviceId]);
  const wasAll = prevAllIds.length > 0 && prevAllIds.every((id) => prev.has(id));
  if (wasAll || prev.size === 0) return new Set(allIds);
  const kept = allIds.filter((id) => prev.has(id));
  return kept.length === 0 ? new Set(allIds) : new Set(kept);
}

export function UptimeByDayCard({
  dailyUptimeByDevice,
  dailyUptime,
  lookbackDailyUptime = [],
  lookbackDailyUptimeByDevice = [],
  devices,
  filters,
  showCriteria = true,
  selectedDeviceIds: selectedDeviceIdsProp,
  onSelectedDeviceIdsChange,
  hideTablePicker = false,
  externallyReconciled = false,
  fullWeeks: fullWeeksProp,
  onFullWeeksChange,
  hideWeekToggle = false,
}: UptimeByDayCardProps) {
  const controlled = selectedDeviceIdsProp !== undefined && onSelectedDeviceIdsChange != null;
  const allIds = useMemo(() => devices.map((d) => d.id).filter(Boolean), [devices]);
  const filterLocksSingle =
    filters.deviceId !== "all" && allIds.some((id) => id === filters.deviceId);

  const [uncontrolledSelectedIds, setUncontrolledSelectedIds] = useState<Set<string>>(
    () => new Set(allIds),
  );
  const fullWeeksControlled = fullWeeksProp !== undefined && onFullWeeksChange != null;
  const [uncontrolledFullWeeks, setUncontrolledFullWeeks] = useState(false);
  const fullWeeks = fullWeeksControlled ? fullWeeksProp : uncontrolledFullWeeks;
  const setFullWeeks = (next: boolean) => {
    if (fullWeeksControlled) onFullWeeksChange(next);
    else setUncontrolledFullWeeks(next);
  };
  const prevAllIdsRef = useRef<string[]>(allIds);

  const selectedIds = controlled ? new Set(selectedDeviceIdsProp) : uncontrolledSelectedIds;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const setSelectedIds = (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    const prev = selectedIdsRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    if (controlled) onSelectedDeviceIdsChange!(next);
    else setUncontrolledSelectedIds(next);
  };

  useEffect(() => {
    if (externallyReconciled) return;
    const next = nextSelectionForDeviceList(
      selectedIdsRef.current,
      prevAllIdsRef.current,
      allIds,
      filterLocksSingle,
      filters.deviceId,
    );
    prevAllIdsRef.current = allIds;
    if (
      next.size === selectedIdsRef.current.size &&
      [...next].every((id) => selectedIdsRef.current.has(id))
    ) {
      return;
    }
    if (controlled) onSelectedDeviceIdsChange?.(next);
    else setUncontrolledSelectedIds(next);
  }, [
    allIds,
    controlled,
    externallyReconciled,
    filterLocksSingle,
    filters.deviceId,
    onSelectedDeviceIdsChange,
  ]);

  const effectiveIds = useMemo(() => {
    if (filterLocksSingle) return new Set([filters.deviceId]);
    return selectedIds;
  }, [filterLocksSingle, filters.deviceId, selectedIds]);

  const selectedCount = effectiveIds.size;
  const allSelected = !filterLocksSingle && selectedCount === allIds.length && allIds.length > 0;

  const rows = useMemo((): DayHours[] => {
    if (selectedCount === 0) return [];
    if (allSelected && dailyUptime.length > 0) {
      return dailyUptime.map((r) => ({
        day: r.day,
        uptimeHours: Math.max(0, r.uptimeHours),
        scheduledHours: Math.max(0, r.scheduledHours),
        capacityHours: Math.max(0, r.capacityHours),
        unmeasuredHours: r.unmeasuredHours,
      }));
    }
    return sumSelectedDailyUptime(dailyUptimeByDevice, effectiveIds);
  }, [allSelected, dailyUptime, dailyUptimeByDevice, effectiveIds, selectedCount]);

  const expectedByDay = useMemo(
    () => sumExpectedUptimeByDay(dailyUptimeByDevice, effectiveIds),
    [dailyUptimeByDevice, effectiveIds],
  );

  /** Display + lookback uptime hours keyed by day (lookback fills −7d points outside the window). */
  const uptimeByDayIncludingLookback = useMemo(() => {
    const map = new Map<string, number>();
    const addRows = (list: DayHours[]) => {
      for (const r of list) {
        if (r.unmeasuredHours === 0 && r.capacityHours > 0) map.set(r.day, r.uptimeHours);
      }
    };
    if (allSelected && lookbackDailyUptime.length > 0) {
      addRows(
        lookbackDailyUptime.map((r) => ({
          day: r.day,
          uptimeHours: Math.max(0, r.uptimeHours),
          scheduledHours: Math.max(0, r.scheduledHours),
          capacityHours: Math.max(0, r.capacityHours),
          unmeasuredHours: r.unmeasuredHours,
        })),
      );
    } else if (lookbackDailyUptimeByDevice.length > 0 && selectedCount > 0) {
      addRows(sumSelectedDailyUptime(lookbackDailyUptimeByDevice, effectiveIds));
    }
    addRows(rows);
    return map;
  }, [
    allSelected,
    effectiveIds,
    lookbackDailyUptime,
    lookbackDailyUptimeByDevice,
    rows,
    selectedCount,
  ]);

  const weeks = useMemo((): UptimeWeekBucket[] => {
    if (!fullWeeks || selectedCount === 0) return [];
    return groupFullMondayWeeks(
      rows.map((row) => ({
        ...row,
        expectedHours: expectedByDay.get(row.day) ?? expectedUptimeHoursForDay(row),
      })),
      selectedCount,
    );
  }, [expectedByDay, fullWeeks, rows, selectedCount]);

  const bars: BarChartItem[] = useMemo(() => {
    if (fullWeeks) {
      return weeks.map((week) => {
        const up = week.uptimeHours;
        const expected = week.expectedHours;
        const priorHours = priorWeekMeasuredUptime(week.monday, uptimeByDayIncludingLookback);
        return {
          label: formatWeekAxisLabel(week.monday, week.sunday),
          value: Math.max(expected, up, 0.0001),
          ...weekBarValueLabel(week),
          stack: [
            { value: expected, fill: EXPECTED_UPTIME_FILL, label: "Expected uptime" },
            { value: up, fill: UPTIME_FILL, label: "Uptime" },
          ],
          ...(priorHours != null ? { referenceValue: priorHours } : {}),
        };
      });
    }
    return rows.map((row) => {
      const axis = dailyChartBarAxisProps(row.day);
      const up = row.uptimeHours;
      const expected = expectedByDay.get(row.day) ?? expectedUptimeHoursForDay(row);
      const priorDay = shiftReportingDayKey(row.day, -7);
      const priorHours = priorDay ? uptimeByDayIncludingLookback.get(priorDay) : undefined;
      return {
        ...axis,
        value: Math.max(expected, up, 0.0001),
        valueLabel: row.unmeasuredHours >= row.capacityHours ? "—" : formatUptimeHoursLabel(up),
        stack: [
          { value: expected, fill: EXPECTED_UPTIME_FILL, label: "Expected uptime" },
          { value: up, fill: UPTIME_FILL, label: "Uptime" },
        ],
        ...(priorHours != null ? { referenceValue: priorHours } : {}),
      };
    });
  }, [expectedByDay, fullWeeks, rows, uptimeByDayIncludingLookback, weeks]);

  const totalUptimeHours = fullWeeks
    ? weeks.reduce((sum, week) => sum + Math.max(0, week.uptimeHours), 0)
    : rows.reduce((sum, r) => sum + Math.max(0, r.uptimeHours), 0);
  const totalExpectedHours = fullWeeks
    ? weeks.reduce((sum, week) => sum + week.expectedHours, 0)
    : rows.reduce((sum, r) => sum + (expectedByDay.get(r.day) ?? expectedUptimeHoursForDay(r)), 0);
  const valueLabelAxisMeta = fullWeeks
    ? `${uptimeRangeLabel(selectedCount, totalUptimeHours, totalExpectedHours)} · full weeks`
    : uptimeRangeLabel(selectedCount, totalUptimeHours, totalExpectedHours);
  const showValueLabels = bars.length > 0 && bars.length <= VALUE_LABEL_MAX_BARS;

  const weekToggle = hideWeekToggle ? (
    fullWeeks ? (
      <span className="text-[12px] font-medium text-text-secondary">Full weeks</span>
    ) : null
  ) : (
    <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] font-medium text-text-primary">
      <input
        type="checkbox"
        className="accent-accent-2"
        checked={fullWeeks}
        onChange={(e) => setFullWeeks(e.target.checked)}
      />
      Full weeks
    </label>
  );

  const tablePicker = (
    <ChartTablePicker
      devices={devices}
      selectedIds={filterLocksSingle ? effectiveIds : selectedIds}
      onSelectedIdsChange={setSelectedIds}
      locked={filterLocksSingle}
      hideInteractive={hideTablePicker}
    />
  );

  return (
    <ChartCard
      title="Uptime"
      criteria={showCriteria ? CRITERIA : undefined}
      note={
        fullWeeks
          ? "Solid = uptime; faded = expected, summed over each complete Monday–Sunday in the range. Numbers above bars are hours and % up vs expected when there are 7 or fewer weeks. Partial weeks at the edges (including this week if it has not finished) are omitted. Dotted line = previous Monday–Sunday. Table checklist is shared via the URL."
          : "Solid = uptime; faded = expected uptime (capacity − scheduled). Today is so far; the gap includes no signal and not measured. Dotted line = same weekday −7d (uses lookback before the range). Table checklist is shared via the URL."
      }
      titleRight={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {weekToggle}
          {tablePicker}
        </div>
      }
    >
      {selectedCount === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">Select at least one table.</p>
      ) : bars.length === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">
          {fullWeeks ? FULL_WEEKS_EMPTY_MESSAGE : "No uptime data yet"}
        </p>
      ) : (
        <BarChart
          data={bars}
          fillBars
          stackOverlay
          hideStackSegmentLabels
          compact={dailyChartRotateAxisLabels(bars.length)}
          verbatimAxisLabels
          rotateAxisLabels={dailyChartRotateAxisLabels(bars.length)}
          axisLabelMaxChars={fullWeeks ? 12 : 10}
          valueLabelAxisMeta={valueLabelAxisMeta}
          showValueLabels={showValueLabels}
          valueLabelAxisTitle={showValueLabels ? (fullWeeks ? "Uptime (%)" : "Uptime") : undefined}
          showYAxis
          formatYAxisTick={(v) => formatUptimeHoursLabel(v)}
          legend={[
            { label: "Uptime", fill: UPTIME_FILL },
            { label: "Expected uptime", fill: EXPECTED_UPTIME_FILL },
            {
              label: "Prior week",
              stroke: PRIOR_WEEK_STROKE,
              strokeDasharray: "4 3",
            },
          ]}
          barTooltip={(item, index) => {
            if (fullWeeks) {
              const week = weeks[index];
              if (!week) return item.label;
              const priorMonday = shiftReportingDayKey(week.monday, -7);
              const priorSunday = priorMonday ? shiftReportingDayKey(priorMonday, 6) : "";
              const prior = priorWeekMeasuredUptime(week.monday, uptimeByDayIncludingLookback);
              const priorBit =
                prior != null && priorMonday
                  ? ` Prior week line: ${formatUptimeHoursLabel(prior)}h (${formatDashboardDayLabelLong(priorMonday)} – ${formatDashboardDayLabelLong(priorSunday)}).`
                  : "";
              const pct = formatUpVsExpectedPct(week.uptimeHours, week.expectedHours);
              const pctBit = pct ? ` (${pct} up vs expected)` : "";
              return `${formatDashboardDayLabelLong(week.monday)} – ${formatDashboardDayLabelLong(week.sunday)}: ${formatUptimeHoursLabel(week.uptimeHours)}h up, ${formatUptimeHoursLabel(week.expectedHours)}h expected uptime${pctBit} (capacity ${formatUptimeHoursLabel(week.capacityHours)}h − scheduled ${formatUptimeHoursLabel(week.scheduledHours)}h) summed across 7 days and ${selectedCount} table${selectedCount === 1 ? "" : "s"}. Not measured: ${formatUptimeHoursLabel(week.unmeasuredHours)}h; included in expected uptime.${priorBit} Platform heartbeat minutes (no Raw Events drill-down).`;
            }
            const row = rows.find((r) => dailyChartAxisLabel(r.day) === item.label);
            if (!row) return item.label;
            const up = formatUptimeHoursLabel(row.uptimeHours);
            const expected = formatUptimeHoursLabel(
              expectedByDay.get(row.day) ?? expectedUptimeHoursForDay(row),
            );
            const sched = formatUptimeHoursLabel(row.scheduledHours);
            const cap = formatUptimeHoursLabel(row.capacityHours);
            const priorDay = shiftReportingDayKey(row.day, -7);
            const prior = priorDay ? uptimeByDayIncludingLookback.get(priorDay) : undefined;
            const priorBit =
              prior != null
                ? ` Prior week line: ${formatUptimeHoursLabel(prior)}h on ${formatDashboardDayLabelLong(priorDay)}.`
                : "";
            return `${formatDashboardDayLabelLong(row.day)}: ${up}h up, ${expected}h expected uptime (capacity ${cap}h − scheduled ${sched}h) across ${selectedCount} table${selectedCount === 1 ? "" : "s"}. Not measured: ${formatUptimeHoursLabel(row.unmeasuredHours)}h; included in expected uptime.${priorBit} Platform heartbeat minutes (no Raw Events drill-down).`;
          }}
        />
      )}
    </ChartCard>
  );
}
